
import React, { useState } from 'react';
import { useBooking } from '../../context/BookingContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { format } from 'date-fns';
import { Calendar, Clock, Loader2, User } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { patientService } from '../../services/patientService';
import { useToast } from '../../hooks/use-toast';

const AppointmentReview: React.FC = () => {
  const { state, goToStep, confirmAppointment } = useBooking();
  const { doctor, date, timeSlot, patientDetails } = state;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleBack = () => {
    goToStep(3);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      // First, create or update patient record
      const patientData = {
        fullName: patientDetails.fullName,
        email: patientDetails.email || '',
        phone: patientDetails.phone,
        address: patientDetails.address || '',
        gender: patientDetails.gender || '',
        age: patientDetails.age ? parseInt(patientDetails.age, 10) : undefined,
        city: patientDetails.city || '',
        state: patientDetails.state || '',
        country: patientDetails.country || '',
        zipCode: patientDetails.zipCode || '',
        notes: patientDetails.notes || '',
      };
      
      // Try to create or update patient
      let patientId;
      try {
        // Create or update patient record
        const patient = await patientService.create(patientData);
        patientId = patient.id;
        console.log('Patient record created/updated:', patient);
      } catch (error) {
        console.error('Error syncing patient data, continuing with appointment booking:', error);
        // Continue with appointment booking even if patient sync fails
      }
      
      // Then confirm the appointment
      await confirmAppointment(patientId);
    } catch (error) {
      console.error('Error during appointment confirmation process:', error);
      toast({
        title: "Appointment booking failed",
        description: "There was an error creating your appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!doctor || !date || !timeSlot) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Missing appointment information. Please go back and complete all steps.</p>
        <Button className="mt-4" onClick={() => goToStep(1)}>Start Over</Button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground">Review Your Appointment</h2>
        <p className="text-muted-foreground">Please confirm your appointment details</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Doctor Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden">
                <img 
                  src={doctor.image} 
                  alt={doctor.name} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-medium">{doctor.name}</h3>
                <p className="text-sm text-medical-700">{doctor.specialty}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>{format(date, 'EEEE, MMMM do, yyyy')}</span>
            </div>
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{timeSlot.time}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Patient Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2">
              <User className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <span className="font-medium block">{patientDetails.fullName}</span>
                {patientDetails.gender && patientDetails.age && (
                  <span className="text-sm text-muted-foreground">
                    ({patientDetails.gender}, {patientDetails.age} years)
                  </span>
                )}
              </div>
            </div>
            
            <div>
              <p className="text-sm">
                <span className="font-medium">Email:</span>{" "}
                <span className="text-muted-foreground">{patientDetails.email}</span>
              </p>
            </div>
            
            <div>
              <p className="text-sm">
                <span className="font-medium">Phone:</span>{" "}
                <span className="text-muted-foreground">{patientDetails.phone}</span>
              </p>
            </div>
            
            {patientDetails.address && (
              <div>
                <p className="text-sm">
                  <span className="font-medium">Address:</span>{" "}
                  <span className="text-muted-foreground">{patientDetails.address}</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardContent className="pt-6">
          <CardDescription className="mb-4 text-center">
            By confirming this appointment, you agree to our terms and conditions. You can cancel or reschedule your appointment up to 24 hours before the scheduled time.
          </CardDescription>
          
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
              Back
            </Button>
            <Button onClick={handleConfirm} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm Appointment'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppointmentReview;
